import * as Sentry from "@sentry/react";
import { z } from "zod";

/**
 * 依頼詳細API（/picture-request/:id、limit/offset 付きの分割取得を含む）の応答契約。
 * count は分割取得のチャンク数の算出に使うため、負数・非整数は契約違反として扱う
 */
const pictureRequestDetailContractSchema = z
  .object({
    isOtherUserResource: z.boolean(),
    // NOTE: count に上限（.max()）は設けない。依頼件数には業務上の上限がない
    // （PO判断）ため恣意的な定数になり、超過時に正常な依頼が契約違反として
    // 表示不能になる。過大な件数によるリクエスト増幅は分割取得側の
    // 空チャンク打ち切り（useChunkedBookDetails）で防ぐ
    count: z.number().int().nonnegative(),
    list: z.array(
      z
        .object({
          isOtherUserResource: z.boolean(),
        })
        .passthrough()
    ),
  })
  .passthrough();

/**
 * 応答契約違反。同じ応答を取り直しても結果は変わらない（決定的な失敗）ため、
 * 呼び出し側はこのエラーに対して自動リトライしない
 */
export class ApiContractViolationError extends Error {
  constructor(endpoint: string) {
    super(`API contract violation at ${endpoint}`);
    this.name = "ApiContractViolationError";
  }
}

/**
 * 依頼詳細APIの応答が契約を満たすことを検証する。
 * 違反時は Sentry に通知したうえで例外を送出し、不正な応答が画面や後続処理に流れないようにする
 */
export const validatePictureRequestDetailContract = (
  payload: unknown,
  endpoint: string
): void => {
  const parsed = pictureRequestDetailContractSchema.safeParse(payload);
  if (parsed.success) return;

  const error = new ApiContractViolationError(endpoint);
  Sentry.captureException(error, {
    extra: {
      endpoint,
      issues: parsed.error.issues,
    },
  });
  throw error;
};
